import { memo, useState, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { RoutePath } from 'consts/RoutePath';
import { Hr } from 'components/atoms/Hr';
import { ApiError } from 'requests/RequestBase';
import { UserWithGroupAuth, callDeleteGroupMemberApi } from 'requests/RequestGroup';
import {
	ServiceAgreement,
	GetServiceAgreementResData,
	callGetServiceAgreementApi,
	callGetIsLockApi,
	GetIsLockResData,
	initialIsLockData,
} from 'requests/RequestUser';
import { findSexTypeNameByKey } from 'consts/SexConst';
import { GroupAuth } from 'consts/GroupAuthConst';
import { HttpStatus } from 'consts/HttpStatus';
import { CircularProgress, Backdrop } from '@material-ui/core';
import { BirthDay } from 'components/molecules/BirthDay';
import classes from './override.module.scss';

type Props = {};
export type MemberRemoveDto = {
	groupId: string;
	targetUser: UserWithGroupAuth;
	loginUser: UserWithGroupAuth;
};

export const MemberRemoveTemplate = memo((_props: Props) => {
	const history = useHistory<MemberRemoveDto>();
	const dto = history.location.state;
	const [page, setPage] = useState(0);
	const [timer, setTimer] = useState(undefined);
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';
	const backToMyPage = () => {
		clearTimeout(timer);
		history.push(RoutePath.MY_PAGE);
	};
	const [deleteLoading, setDeleteLoading] = useState(false);
	const deleteMember = async () => {
		setDeleteLoading(true);
		// メンバー削除
		const result = await callDeleteGroupMemberApi(dto.groupId, dto.targetUser.gidpk);
		if (result.status !== HttpStatus.OK) {
			const errorData = result.data as ApiError;
			setActionError(errorData.message);
			setDeleteLoading(false);
			return;
		}
		setDeleteLoading(false);

		const timer = setTimeout(function () {
			history.push(RoutePath.MY_PAGE);
		}, 5000);
		setTimer(timer);

		setPage((prev) => prev + 1);
	};

	const [isGetDataLoading, setIsGetDataLoading] = useState(false);
	const [lockedServices, setLockedServices] = useState<ServiceAgreement[]>([]);
	const [isLockResData, setIsLockResData] = useState<GetIsLockResData>(initialIsLockData);
	const scrollRef = useRef(null);
	useEffect(() => {
		const execute = async () => {
			setIsGetDataLoading(true);

			const results = await Promise.all([
				callGetServiceAgreementApi({ gidpklist: dto.targetUser.gidpk }),
				callGetIsLockApi({ gidpk: dto.targetUser.gidpk }),
			]);
			const errorResult = results.find((r) => r.status !== HttpStatus.OK);
			if (errorResult) {
				const errorData = errorResult.data as ApiError;
				console.log(errorData.message);
				history.push(RoutePath.ERROR);
				setIsGetDataLoading(false);
				return;
			}

			const [serviceAgreementResult, isLockResult] = results;
			const serviceAgreementData = serviceAgreementResult.data as GetServiceAgreementResData;
			setLockedServices(serviceAgreementData.gid_list[0].service_agreement_list.filter((d) => d.withdrawal_lock === 1));
			const isLockData = isLockResult.data as GetIsLockResData;
			setIsLockResData(isLockData);

			setIsGetDataLoading(false);
		};
		execute();

		scrollRef.current.scrollIntoView();
		document.title = `Gakken${GroupAuth.MEMBER.name}の削除`;
	}, []);

	const isServiceLock = !!lockedServices?.length;
	// この関数が呼ばれる段階では必ずisServiceLockを満たす
	const serviceLockNote = () => {
		return (
			<>
				<div className={classes.greenBack}>
					<p className={classes.greenBackTitle}>現在ご利用中のサービス</p>
					<ul>
						{lockedServices.map((s) => {
							return (
								<li>
									<a href={s.setting_change_url} target="_blank">
										{s.display_site_name}
									</a>
								</li>
							);
						})}
					</ul>
				</div>
				<div className="section-txt-box" style={{ marginBottom: 40 }}>
					<div>ご利用いただいている上記サービスの登録解除・解約が必要となります。</div>
					<div>登録解除・解約については各サービスの事務局へお問い合わせください。</div>
					<div>また、上記以外の各サービスの登録情報がある場合も、サービスサイト内で別途お手続きください。</div>
				</div>
			</>
		);
	};

	const isFamilyLock = isLockResData.is_lock;
	// この関数が呼ばれる段階では必ずisFamilyLockを満たす
	const familyLockNote = () => {
		return (
			<>
				<div className={classes.greenBack}>
					<p className={classes.greenBackTitle}>
						{`家族構成を変更できないため、GakkenこどもIDを削除することができません`}
					</p>
				</div>
				<div className="section-txt-box" style={{ marginBottom: 40 }}>
					<div>ご利用いただいているサービスの登録解除・解約が必要となります。</div>
					<div>登録解除・解約については各サービスの事務局へお問い合わせください。</div>
				</div>
			</>
		);
	};

	const errorRef = useRef(null);
	useEffect(() => {
		if (isActionError) {
			errorRef.current.scrollIntoView();
		}
	}, [actionError]);

	return (
		<>
			<Backdrop className={classes.backdrop} open={isGetDataLoading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<section className="section mypage" ref={scrollRef}>
				<div className="breadcrumb">
					<ul>
						<li>
							<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
						</li>
						<li>
							<Link to={RoutePath.FAMILY_INFO}>家族情報</Link>
						</li>
						<li>
							<Link to={{ pathname: RoutePath.MEMBER_INFO, state: dto }}>ユーザー情報</Link>
						</li>
						<li>{`Gakken${GroupAuth.MEMBER.name}の削除`}</li>
					</ul>
				</div>
				<h1 className="page-head">GakkenこどもIDの削除</h1>
				{page === 0 && (
					<div className="section-in" style={{ marginTop: 50 }}>
						<div className="section-head error-box">
							<p>注意事項をご確認ください</p>
						</div>
						<div className="section-txt-box" style={{ marginBottom: 40 }}>
							<div className="txt">
								<div>GakkenこどもIDを削除すると以下のことができなくなります。</div>
								<ul>
									<li>現在ご利用いただいている「サービスの利用」</li>
								</ul>
							</div>
						</div>

						{isServiceLock || isFamilyLock ? (
							<>
								{isServiceLock && serviceLockNote()}
								{isServiceLock && isFamilyLock && <Hr />}
								{isFamilyLock && familyLockNote()}
							</>
						) : null}

						{isActionError ? (
							<div className="error-box" ref={errorRef}>
								<p>{actionError}</p>
							</div>
						) : null}

						<div className="info-box">
							<div className="info-head">GakkenID</div>
							<div className="info-txt">{dto.targetUser.gid}</div>
							<Hr />
							<div className="info-head">パスワード</div>
							<div className="info-txt">*************</div>
							<Hr />
							<div className="info-head">お名前（全角）</div>
							<div className="info-txt">{`${dto.targetUser.lastname}　${dto.targetUser.firstname}`}</div>
							<Hr />
							<div className="info-head">お名前（カナ）</div>
							<div className="info-txt">{`${dto.targetUser.kana_lastname}　${dto.targetUser.kana_firstname}`}</div>
							<Hr />
							<div className="info-head">生年月日</div>
							<div className="info-txt">
								<div className="info-txt">
									<BirthDay birth={dto.targetUser.birth} isFlexStart={true} />
								</div>
							</div>
							<Hr />
							<div className="info-head">性別</div>
							<div className="info-txt">{findSexTypeNameByKey(dto.targetUser.sex) || '　'}</div>
							<div className="btn-box">
								{deleteLoading ? (
									<CircularProgress />
								) : (
									<>
										<div className="btn wh">
											<Link to={RoutePath.MY_PAGE}>
												<span>戻る</span>
											</Link>
										</div>
										{!isServiceLock && !isFamilyLock ? (
											<div className="btn gr">
												<button onClick={deleteMember}>削除</button>
											</div>
										) : null}
									</>
								)}
							</div>
						</div>
					</div>
				)}
				{page === 1 && (
					<div className="section-in" style={{ marginTop: 50 }}>
						<div className="thankyou-box">
							<img src="images/img-complete.svg" alt="完了しました" />
						</div>
						<p>
							<div>5秒後に自動的にマイページTOPへ戻ります</div>
						</p>
						<div className="btn-box">
							<div className="btn gr">
								<button onClick={backToMyPage}>マイページTOPへ戻る</button>
							</div>
						</div>
					</div>
				)}
			</section>
		</>
	);
});
